import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import gradesService from '../../services/gradesService';
import DialogModal from '../../components/DialogModal';
import {
  DataTable,
  FormInput,
  FormSelect,
} from '../../components/finance';
import { Plus, Pencil, Trash, X, Download, Upload, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import './TeacherGradesSection.css';

function TeacherGradesSection({ taughtClasses }) {
  const [view, setView] = useState('list'); // list, record, details
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [confirmDeleteGradeId, setConfirmDeleteGradeId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Grade threshold (below this is flagged as struggling)
  const PERFORMANCE_THRESHOLD = 60;

  // GES 1-9 grading scale, fetched from the backend so it always matches what
  // students/parents see on report cards and dashboards instead of drifting
  // out of sync with a locally hardcoded A+/A/B... scale.
  const [gesScale, setGesScale] = useState([]);

  useEffect(() => {
    gradesService.getGesScale()
      .then(data => setGesScale(data || []))
      .catch(err => console.error('Error fetching GES grading scale:', err));
  }, []);

  const [formData, setFormData] = useState({
    student_id: '',
    assessment_type: 'class_work',
    score: '',
    max_score: 100,
    weight: 1.0,
  });

  // Helper: Convert score to GES grade (1-9)
  const getLetterGrade = (score, maxScore = 100) => {
    if (score === '-' || score === '' || !score || gesScale.length === 0) return '-';
    const percentage = (parseFloat(score) / parseFloat(maxScore)) * 100;
    const gradeObj = gesScale.find(g => percentage >= g.min_score && percentage <= g.max_score);
    return gradeObj ? gradeObj.grade : '9';
  };

  // Helper: Get grade label
  const getGradeLabel = (score, maxScore = 100) => {
    if (score === '-' || score === '' || !score || gesScale.length === 0) return '-';
    const percentage = (parseFloat(score) / parseFloat(maxScore)) * 100;
    const gradeObj = gesScale.find(g => percentage >= g.min_score && percentage <= g.max_score);
    return gradeObj ? gradeObj.description : 'Fail';
  };

  // Calculate statistics
  const calculateStatistics = (gradesData) => {
    if (!gradesData || gradesData.length === 0) {
      return null;
    }

    const validGrades = gradesData
      .filter(g => g.score !== '-' && g.score !== '' && g.score)
      .map(g => {
        const maxScore = parseFloat(g.max_score) || 100;
        return (parseFloat(g.score) / maxScore) * 100;
      });

    if (validGrades.length === 0) return null;

    const average = validGrades.reduce((a, b) => a + b, 0) / validGrades.length;
    const highest = Math.max(...validGrades);
    const lowest = Math.min(...validGrades);
    const belowThreshold = validGrades.filter(g => g < PERFORMANCE_THRESHOLD).length;

    return {
      average: average.toFixed(1),
      highest: highest.toFixed(1),
      lowest: lowest.toFixed(1),
      total: validGrades.length,
      belowThreshold: belowThreshold
    };
  };

  // Load grades for selected class and subject
  const loadGradesByClass = (classId, subjectId, academicTermId) => {
    setLoading(true);
    const params = {};
    if (subjectId) params.subject_id = subjectId;
    if (academicTermId) params.academic_term_id = academicTermId;
    gradesService.getClassGrades(classId, params)
      .then(data => {
        setClassStudents(data.students || []);
        
        const tableData = [];
        (data.students || []).forEach(student => {
          if (student.grades && student.grades.length > 0) {
            student.grades.forEach(grade => {
              tableData.push({
                ...grade,
                student_id: student.student_id,
                student_name: student.student_name,
                grade_id: grade.id,
              });
            });
          } else {
            tableData.push({
              student_id: student.student_id,
              student_name: student.student_name,
              assessment_type: '-',
              score: '-',
              max_score: '-',
              weight: '-',
              recorded_at: '-',
              grade_id: null,
            });
          }
        });
        setGrades(tableData);
        setStatistics(calculateStatistics(tableData));
        setSelectedClass(classId);
        setSelectedSubject(subjectId);
        setSelectedAcademicTerm(academicTermId);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load grades');
      })
      .finally(() => setLoading(false));
  };



  // Handle record grade
  const handleRecordGrade = async (e) => {
    e.preventDefault();
    
    if (!selectedClass || !selectedSubject) {
      toast.error('Please select a class and subject first');
      return;
    }

    if (!formData.student_id || !formData.score) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const gradePayload = {
        ...formData,
        class_id: selectedClass,
        subject_id: selectedSubject,
        // Editing an existing grade keeps its original term; only a brand-new
        // grade is recorded against the currently-selected term.
        academic_term_id: editingGrade ? editingGrade.academic_term_id : selectedAcademicTerm,
        student_id: formData.student_id,
        assessment_type: formData.assessment_type.toLowerCase(),
        score: parseFloat(formData.score),
        max_score: parseFloat(formData.max_score),
        weight: parseFloat(formData.weight),
      };

      if (editingGrade) {
        await gradesService.updateGrade(editingGrade.grade_id, gradePayload);
        toast.success('Grade updated successfully');
      } else {
        await gradesService.createGrade(gradePayload);
        toast.success('Grade recorded successfully');
      }

      setShowRecordModal(false);
      resetForm();
      loadGradesByClass(selectedClass, selectedSubject, selectedAcademicTerm);
    } catch (err) {
      console.error(err);
      toast.error('Failed to record grade');
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const element = document.querySelector('.grades-table-container');
      if (!element) {
        toast.error('No grades to export');
        return;
      }

      const printWindow = window.open('', '', 'height=600,width=900');
      const htmlContent = `
        <html>
          <head>
            <title>Gradebook</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #333; text-align: center; }
              .stats { margin: 20px 0; padding: 15px; background: #f9fafb; border-radius: 5px; }
              .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
              .stat-box { padding: 10px; background: white; border-left: 3px solid #0066cc; border-radius: 3px; }
              .stat-value { font-size: 20px; font-weight: bold; color: #0066cc; }
              .stat-label { font-size: 12px; color: #666; margin-top: 5px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
              th { background-color: #0066cc; color: white; font-weight: bold; }
              tr:nth-child(even) { background-color: #f9fafb; }
              .struggling { background-color: #ffe0e0; }
            </style>
          </head>
          <body>
            <h1>Gradebook Report</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            ${statistics ? `
              <div class="stats">
                <div class="stats-grid">
                  <div class="stat-box">
                    <div class="stat-value">${statistics.average}%</div>
                    <div class="stat-label">Class Average</div>
                  </div>
                  <div class="stat-box">
                    <div class="stat-value">${statistics.highest}%</div>
                    <div class="stat-label">Highest Score</div>
                  </div>
                  <div class="stat-box">
                    <div class="stat-value">${statistics.lowest}%</div>
                    <div class="stat-label">Lowest Score</div>
                  </div>
                  <div class="stat-box">
                    <div class="stat-value">${statistics.belowThreshold}</div>
                    <div class="stat-label">Below Threshold</div>
                  </div>
                </div>
              </div>
            ` : ''}
            ${element.innerHTML}
          </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.print();
      toast.success('Gradebook exported to PDF');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export gradebook');
    }
  };

  // Handle CSV import
  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const importedGrades = [];
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(',').map(v => v.trim());
          const gradeData = {};
          headers.forEach((header, idx) => {
            gradeData[header] = values[idx];
          });

          if (gradeData.student_id && gradeData.score) {
            importedGrades.push({
              student_id: gradeData.student_id,
              assessment_type: gradeData.assessment_type || 'class_work',
              score: parseFloat(gradeData.score),
              max_score: parseFloat(gradeData.max_score) || 100,
              weight: parseFloat(gradeData.weight) || 1.0,
              class_id: selectedClass,
              subject_id: selectedSubject,
              academic_term_id: selectedAcademicTerm,
            });
          }
        }

        if (importedGrades.length === 0) {
          toast.error('No valid grades found in file');
          return;
        }

        // Submit all grades
        try {
          for (const grade of importedGrades) {
            await gradesService.createGrade(grade);
          }
          toast.success(`Successfully imported ${importedGrades.length} grades`);
          setShowCsvModal(false);
          setCsvFile(null);
          loadGradesByClass(selectedClass, selectedSubject, selectedAcademicTerm);
        } catch (err) {
          console.error(err);
          toast.error('Failed to import some grades');
        }
      };
      reader.readAsText(csvFile);
    } catch (err) {
      console.error(err);
      toast.error('Failed to import file');
    }
  };

  const resetForm = () => {
    setFormData({
      student_id: '',
      assessment_type: 'class_work',
      score: '',
      max_score: 100,
      weight: 1.0,
    });
    setEditingGrade(null);
  };

  // Download CSV template
  const downloadTemplate = () => {
    try {
      const csvContent = `student_id,assessment_type,score,max_score,weight
STU001,class_work,85,100,1.0
STU002,homework,92,100,0.5
STU003,quiz,78,100,1.0
STU004,mid_term,88,100,2.0
STU005,project,95,100,1.5`;

      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
      element.setAttribute('download', 'grades_template.csv');
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
      toast.success('Template downloaded successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to download template');
    }
  };

  const handleEditGrade = (grade) => {
    setFormData({
      student_id: grade.student_id,
      assessment_type: grade.assessment_type,
      score: grade.score,
      max_score: grade.max_score,
      weight: grade.weight,
    });
    // Keep the grade's own term rather than the currently-selected one, so saving
    // an edit to a grade from a past term doesn't silently move it into the
    // current term.
    setEditingGrade(grade);
    setShowRecordModal(true);
  };

  const handleDeleteGrade = (gradeId) => {
    setConfirmDeleteGradeId(gradeId);
  };

  const confirmDeleteGrade = async () => {
    if (!confirmDeleteGradeId) return;

    setConfirmLoading(true);
    try {
      await gradesService.deleteGrade(confirmDeleteGradeId);
      toast.success('Grade deleted successfully');
      loadGradesByClass(selectedClass, selectedSubject, selectedAcademicTerm);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete grade');
    } finally {
      setConfirmLoading(false);
      setConfirmDeleteGradeId(null);
    }
  };

  // View List
  if (view === 'list') {
    return (
      <div className="teacher-grades-section">
        <div className="section-header">
          <h2>Manage Grades</h2>
          <div className="header-actions">
            <button 
              className="btn btn-primary" 
              onClick={() => {
                resetForm();
                setShowRecordModal(true);
              }}
              disabled={!selectedClass}
              title={selectedClass ? 'Record a new grade' : 'Select a class first'}
            >
              <Plus size={18} />
              Record Grade
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowCsvModal(true)}
              disabled={!selectedClass}
              title="Bulk import grades from CSV"
            >
              <Upload size={18} />
              Import CSV
            </button>
            <button
              className="btn btn-secondary"
              onClick={exportToPDF}
              disabled={!selectedClass}
              title="Export grades to PDF"
            >
              <Download size={18} />
              Export PDF
            </button>
          </div>
        </div>

        {/* Class Selection */}
        <div className="class-selector">
          <h3>Select a Class</h3>
          <div className="class-buttons">
            {taughtClasses.map(cls => (
              <button
                key={cls.assignment_id}
                className={`class-btn ${selectedClass === cls.class_id && selectedSubject === cls.subject_id ? 'active' : ''}`}
                onClick={() => {
                  loadGradesByClass(cls.class_id, cls.subject_id, cls.academic_term_id);
                }}
              >
                <span className="class-name">{cls.class_name}</span>
                <span className="subject-name">{cls.subject_name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Statistics Card */}
        {selectedClass && statistics && (
          <div className="statistics-card">
            <h3>Class Statistics</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{statistics.average}%</div>
                <div className="stat-label">Average</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{statistics.highest}%</div>
                <div className="stat-label">Highest</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{statistics.lowest}%</div>
                <div className="stat-label">Lowest</div>
              </div>
              <div className="stat-item">
                <div className="stat-value" style={{ color: statistics.belowThreshold > 0 ? '#dc2626' : '#059669' }}>
                  {statistics.belowThreshold}
                </div>
                <div className="stat-label">Below Threshold</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{statistics.total}</div>
                <div className="stat-label">Total Grades</div>
              </div>
            </div>
          </div>
        )}

        {/* Grades Table */}
        {selectedClass && (
          <div className="grades-table-container">
            {loading ? (
              <div className="loading">Loading grades...</div>
            ) : classStudents.length > 0 ? (
              <div>
                <table className="grades-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Assessment Type</th>
                      <th>Score</th>
                      <th>Letter Grade</th>
                      <th>Max Score</th>
                      <th>Weight</th>
                      <th>Recorded</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map((row, idx) => {
                      const percentage = row.score !== '-' && row.score ? (parseFloat(row.score) / parseFloat(row.max_score || 100)) * 100 : 0;
                      const isStruggling = percentage > 0 && percentage < PERFORMANCE_THRESHOLD;
                      return (
                        <tr 
                          key={idx} 
                          className={`${row.grade_id ? 'grade-row' : 'no-grade-row'} ${isStruggling ? 'struggling-row' : ''}`}
                        >
                          <td className="student-name-col">
                            {row.student_name}
                            {isStruggling && (
                              <span className="performance-flag" title="Below performance threshold">
                                <WarningCircle size={16} />
                              </span>
                            )}
                          </td>
                          <td>{row.assessment_type}</td>
                          <td>{row.score}</td>
                          <td className="letter-grade">
                            <span className={`grade-badge grade-${getLetterGrade(row.score, row.max_score).toLowerCase()}`}>
                              {getLetterGrade(row.score, row.max_score)}
                            </span>
                          </td>
                          <td>{row.max_score}</td>
                          <td>{row.weight}</td>
                          <td>{row.recorded_at || '-'}</td>
                          <td>
                            {row.grade_id ? (
                              <div className="action-buttons">
                                <button 
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleEditGrade(row)}
                                  title="Edit grade"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteGrade(row.grade_id)}
                                  title="Delete grade"
                                >
                                  <Trash size={16} />
                                </button>
                              </div>
                            ) : (
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                  setFormData({ ...formData, student_id: row.student_id });
                                  setShowRecordModal(true);
                                }}
                                title="Record a grade for this student"
                              >
                                Add Grade
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No students in this class</p>
              </div>
            )}
          </div>
        )}

        {/* Record Grade Modal - Custom Implementation */}
        {showRecordModal && (
          <div className="modal-overlay" onClick={() => {
            setShowRecordModal(false);
            resetForm();
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingGrade ? 'Edit Grade' : 'Record New Grade'}</h2>
                <button 
                  className="modal-close-btn"
                  onClick={() => {
                    setShowRecordModal(false);
                    resetForm();
                  }}
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleRecordGrade} className="grade-form">
                <div className="form-group">
                  <label>Student *</label>
                  <select
                    required
                    value={formData.student_id}
                    onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                    className="form-input"
                  >
                    <option value="">Select a student</option>
                    {classStudents.map(s => (
                      <option key={s.student_id} value={s.student_id}>
                        {s.student_name} ({s.student_number})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Assessment Type *</label>
                  <select
                    required
                    value={formData.assessment_type}
                    onChange={(e) => setFormData({ ...formData, assessment_type: e.target.value })}
                    className="form-input"
                  >
                    <option value="class_work">Class Work</option>
                    <option value="homework">Homework</option>
                    <option value="quiz">Quiz</option>
                    <option value="mid_term">Mid-Term</option>
                    <option value="end_of_term">End of Term</option>
                    <option value="project">Project</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Score *</label>
                  <input
                    type="number"
                    required
                    step="0.5"
                    min="0"
                    value={formData.score}
                    onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                    className="form-input"
                    placeholder="Enter score"
                  />
                </div>

                <div className="form-group">
                  <label>Max Score *</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    value={formData.max_score}
                    onChange={(e) => setFormData({ ...formData, max_score: e.target.value })}
                    className="form-input"
                    placeholder="Enter max score"
                  />
                </div>

                <div className="form-group">
                  <label>Weight (0-1)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                    className="form-input"
                    placeholder="Enter weight"
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => {
                      setShowRecordModal(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingGrade ? 'Update Grade' : 'Record Grade'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* CSV Import Modal */}
        {showCsvModal && (
          <div className="modal-overlay" onClick={() => setShowCsvModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Import Grades from CSV</h2>
                <button 
                  className="modal-close-btn"
                  onClick={() => {
                    setShowCsvModal(false);
                    setCsvFile(null);
                  }}
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="modal-body">
                <div className="csv-info">
                  <h4>CSV Format</h4>
                  <p>Your CSV file should have these columns:</p>
                  <code>student_id, assessment_type, score, max_score, weight</code>
                  <p style={{ marginTop: '12px', fontSize: '12px' }}>
                    Example:<br />
                    STU001, class_work, 85, 100, 1.0<br />
                    STU002, quiz, 78, 100, 0.5
                  </p>
                  <button
                    type="button"
                    className="download-template-btn"
                    onClick={downloadTemplate}
                    title="Download CSV template"
                  >
                    <Download size={14} />
                    Download Template
                  </button>
                </div>

                <div className="file-upload">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className="file-input"
                  />
                  {csvFile && (
                    <p style={{ fontSize: '12px', color: '#059669', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <CheckCircle size={14} /> {csvFile.name} selected
                    </p>
                  )}
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCsvModal(false);
                      setCsvFile(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCsvImport}
                    disabled={!csvFile}
                  >
                    <Upload size={16} />
                    Import Grades
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      <DialogModal
        isOpen={Boolean(confirmDeleteGradeId)}
        onClose={() => setConfirmDeleteGradeId(null)}
        title="Delete Grade"
        message="Are you sure you want to delete this grade? This action cannot be undone."
        type="warning"
        primaryAction={{ label: confirmLoading ? 'Deleting...' : 'Delete', onClick: confirmDeleteGrade }}
        secondaryAction={{ label: 'Cancel' }}
        showCloseButton={!confirmLoading}
        isLoading={confirmLoading}
        disableCloseWhenLoading={true}
      />
      </div>
    );
  }

  return null;
}

export default TeacherGradesSection;
